
import React, { ReactNode } from 'react';
import { useLocation, Outlet } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import GradientBackground from './GradientBackground';

interface LayoutProps {
  children?: ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const isDark = theme === 'dark';
  
  // Don't show footer on chat pages
  const isChatPage = location.pathname === '/chat';
  
  // Check if on Terms or Privacy pages
  const isLegalPage = location.pathname === '/terms' || location.pathname === '/privacy';
  
  // Check if on Profile page or Settings page
  const isProfilePage = location.pathname === '/profile' || location.pathname === '/settings';
  
  // Check if on index page
  const isIndexPage = location.pathname === '/';

  // Check if on login or signup page - we'll still show navbar here
  const isAuthPage = location.pathname === '/login' || location.pathname === '/signup';
  
  // Page transition variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1, transition: { duration: 0.4 } },
    exit: { opacity: 0, transition: { duration: 0.3 } }
  };
  
  return (
    <div className={cn(
      "min-h-screen flex flex-col relative",
      isDark
        ? 'bg-[#0F1225] text-slate-200' 
        : 'bg-[#F5F5F7] text-[#1D1D1F]'
    )}>
      {/* Apply background only for non-chat pages */}
      {!isChatPage && <GradientBackground />}
      
      {/* Navbar is always shown, even on auth pages */}
      <Navbar />
      
      <motion.main 
        initial="initial"
        animate="animate"
        exit="exit"
        variants={pageVariants}
        className={cn(
          "flex-grow w-full",
          isDark 
            ? isIndexPage ? 'bg-gradient-radial from-[#171C3C]/50 to-[#0F1225]/80' : ''
            : isIndexPage ? 'bg-gradient-radial from-[#F5F5F7]/50 to-white/80' : ''
        )}
      >
        {children || <Outlet />}
      </motion.main>
      
      {!isChatPage && <Footer />}
    </div>
  );
};

export default Layout;
